import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import TvIcon from "@mui/icons-material/TvOutlined";
import SignageIcon from "@mui/icons-material/ViewCarouselOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import DangerConfirmDialog from "../components/DangerConfirmDialog";
import { ItemFormSurface } from "../components/EditDrawer";
import type { PremiseConfig } from "@shared/types/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import LivePreview from "../components/template/LivePreview";
import { foyerApi, type CompanySummary } from "../http/foyer";
import {
  demoStudioApi,
  type DemoSessionSummary,
  type DemoSessionView,
  type DemoTurnEvent,
} from "../http/demoStudio";
import { openPreview, openSignagePreview } from "../utils/preview";
import { friendlyError } from "../utils/apiError";
import { useFormat } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";
import { PermissionGate } from "../auth/access";

/**
 * Demo Studio: a salesperson pastes a hotel's website, Claude builds a sales
 * demo under Nenda Demo, and the chat refines it.
 *
 * The page never writes a premise itself. It starts sessions and turns; the
 * server's agent writes a spec and applies it (demos only - see
 * services/demo/demoStudio.ts), and the page re-reads the premise to show it.
 */
export default function DemoStudioPage() {
  const { t } = useTranslation(["users", "common"]);
  const { sessionId } = useParams();
  const [availability, setAvailability] = useState<{
    available: boolean;
    reason?: string;
  } | null>(null);

  useEffect(() => {
    demoStudioApi
      .availability()
      .then(setAvailability)
      .catch((e) =>
        setAvailability({ available: false, reason: friendlyError(t, e) })
      );
  }, [t]);

  const [newOpen, setNewOpen] = useState(false);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("demoStudioPage.eyebrow")}
        title={t("demoStudioPage.title")}
        subtitle={t("demoStudioPage.subtitle")}
        actions={
          sessionId ? undefined : (
            <PermissionGate resource="demos" level="administrate" place="nenda">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setNewOpen(true)}
                data-testid="demo-new"
              >
                {t("demoStudioPage.newDemo")}
              </Button>
            </PermissionGate>
          )
        }
      />
      {availability && !availability.available && (
        <Alert severity="warning">
          {t("demoStudioPage.unavailable", { reason: availability.reason })}
        </Alert>
      )}
      {sessionId ? (
        <SessionView key={sessionId} sessionId={sessionId} />
      ) : (
        <StartView />
      )}
      {newOpen && (
        <NewDemoDialog
          disabled={!availability?.available}
          onClose={() => setNewOpen(false)}
        />
      )}
    </PageContainer>
  );
}

/**
 * "New demo" creates a session, so it is a modal. On success it fades out and
 * then opens the new session page, which starts the first turn.
 */
function NewDemoDialog({
  disabled,
  onClose,
}: {
  disabled: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(): Promise<string | null> {
    setBusy(true);
    setError(null);
    try {
      const { id } = await demoStudioApi.create(url.trim());
      return id;
    } catch (e) {
      setError(friendlyError(t, e));
      setBusy(false);
      return null;
    }
  }

  return (
    <ItemFormSurface
      mode="create"
      onClose={onClose}
      busy={busy}
      title={t("demoStudioPage.newDemo")}
      data-testid="demo-new-dialog"
      actions={(dismiss) => (
        <>
          <Button color="inherit" disabled={busy} onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AutoAwesomeIcon />}
            disabled={
              disabled || busy || !/^https?:\/\/\S+\.\S+/.test(url.trim())
            }
            onClick={async () => {
              const id = await start();
              // The note rides along to the session page, which starts the
              // first turn.
              if (id) {
                dismiss(() =>
                  navigate(`/demo-studio/${id}`, {
                    state: { firstNote: note.trim() },
                  })
                );
              }
            }}
            data-testid="demo-start"
          >
            {t("demoStudioPage.newDialog.build")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label={t("demoStudioPage.newDialog.website")}
          placeholder="https://www.example-hotel.se/"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          inputProps={{ "data-testid": "demo-url" }}
          fullWidth
        />
        <TextField
          label={t("demoStudioPage.newDialog.note")}
          placeholder={t("demoStudioPage.newDialog.notePlaceholder")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2" color="text.secondary">
          {t("demoStudioPage.newDialog.takesMinutes")}
        </Typography>
      </Stack>
    </ItemFormSurface>
  );
}

function StartView() {
  const { t } = useTranslation("users");
  const format = useFormat();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<DemoSessionSummary[] | null>(null);

  useEffect(() => {
    demoStudioApi
      .list()
      .then(setSessions)
      .catch(() => setSessions([]));
  }, []);

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t("demoStudioPage.recentDemos")}
          </Typography>
          {!sessions ? (
            <CircularProgress size={20} />
          ) : sessions.length === 0 ? (
            <Typography color="text.secondary">
              {t("demoStudioPage.noneYet")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("demoStudioPage.columns.website")}</TableCell>
                  <TableCell>{t("demoStudioPage.columns.demo")}</TableCell>
                  <TableCell>{t("demoStudioPage.columns.by")}</TableCell>
                  <TableCell>{t("demoStudioPage.columns.updated")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow
                    key={s.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/demo-studio/${s.id}`)}
                  >
                    <TableCell>{s.sourceUrl}</TableCell>
                    <TableCell>
                      {s.premiseSlug ?? "-"}
                      {s.running && (
                        <Chip
                          size="small"
                          label={t("demoStudioPage.working")}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{s.createdBy}</TableCell>
                    <TableCell>{format.dateTime(s.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

const TOOL_NAMES = new Set([
  "write_spec",
  "patch_spec",
  "apply_spec",
  "list_page_images",
  "inspect_image",
  "get_spec",
]);

/** One progress line in the live feed. */
function describe(t: Translate, e: DemoTurnEvent): string | null {
  switch (e.event) {
    case "web":
      return e.data.kind === "search"
        ? t("demoStudioPage.feed.searching", { detail: e.data.detail })
        : t("demoStudioPage.feed.reading", { detail: e.data.detail });
    case "tool_call":
      return TOOL_NAMES.has(e.data.name)
        ? translateKey(t, `users:demoStudioPage.feed.tools.${e.data.name}`)
        : e.data.name;
    case "tool_result":
      return e.data.ok
        ? null
        : t("demoStudioPage.feed.fixing", {
            detail: e.data.content.split("\n")[0].slice(0, 160),
          });
    default:
      return null;
  }
}

function SessionView({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation(["users", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const firstNote = (location.state as { firstNote?: string } | null)
    ?.firstNote;
  const [session, setSession] = useState<DemoSessionView | null>(null);
  const [premise, setPremise] = useState<PremiseConfig | null>(null);
  const [feed, setFeed] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const started = useRef(false);

  const reload = useCallback(async () => {
    const s = await demoStudioApi.get(sessionId);
    setSession(s);
    if (s.premiseSlug && s.lastAppliedAt) {
      setPremise(await foyerApi.getPremise(s.premiseSlug).catch(() => null));
      setPreviewKey((k) => k + 1);
    }
    return s;
  }, [sessionId]);

  const run = useCallback(
    async (body: { message?: string; note?: string }) => {
      setWorking(true);
      setError(null);
      setFeed([]);
      try {
        await demoStudioApi.turn(sessionId, body, (e) => {
          const line = describe(t, e);
          if (line) setFeed((f) => [...f.slice(-30), line]);
          if (
            e.event === "tool_result" &&
            e.data.name === "apply_spec" &&
            e.data.ok
          ) {
            void reload();
          }
          if (e.event === "error") setError(e.data.message);
        });
      } catch (err) {
        setError(friendlyError(t, err));
      } finally {
        setWorking(false);
        await reload().catch(() => undefined);
      }
    },
    [sessionId, reload, t]
  );

  useEffect(() => {
    reload()
      .then((s) => {
        // A brand-new session starts its first turn on arrival.
        if (!started.current && s.chat.length === 0 && !s.running) {
          started.current = true;
          void run({ note: firstNote });
        }
      })
      .catch((e) => setError(friendlyError(t, e)));
    // Once per mount: `firstNote` only matters for the very first turn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, run]);

  // A turn running elsewhere (another tab, a dropped connection): poll.
  useEffect(() => {
    if (!session?.running || working) return;
    const iv = window.setInterval(() => void reload(), 5000);
    return () => window.clearInterval(iv);
  }, [session?.running, working, reload]);

  async function send() {
    const message = draft.trim();
    if (!message) return;
    setDraft("");
    setSession((s) =>
      s
        ? {
            ...s,
            chat: [
              ...s.chat,
              { role: "user", text: message, at: new Date().toISOString() },
            ],
          }
        : s
    );
    await run({ message });
  }

  async function undo() {
    setWorking(true);
    try {
      await demoStudioApi.undo(sessionId);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setWorking(false);
      await reload();
    }
  }

  if (!session) {
    return error ? (
      <Alert severity="error">{error}</Alert>
    ) : (
      <CircularProgress />
    );
  }
  const busy = working || session.running;
  const room = session.presenterRoom ?? session.firstRoom;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        gridTemplateColumns: {
          xs: "1fr",
          lg: "minmax(360px, 1fr) minmax(480px, 1.4fr)",
        },
        alignItems: "start",
      }}
    >
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button size="small" onClick={() => navigate("/demo-studio")}>
                {t("demoStudioPage.allDemos")}
              </Button>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ flex: 1 }}
              >
                {session.sourceUrl}
              </Typography>
            </Stack>

            <Stack spacing={1.5} data-testid="demo-chat">
              {session.chat.map((c, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: c.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "90%",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor:
                      c.role === "user"
                        ? "action.selected"
                        : "background.default",
                    border: 1,
                    borderColor: c.stop === "error" ? "error.main" : "divider",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <Typography variant="body2">{c.text}</Typography>
                </Box>
              ))}
            </Stack>

            {busy && (
              <Box
                sx={{ borderLeft: 2, borderColor: "secondary.main", pl: 1.5 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <CircularProgress size={14} />
                  <Typography variant="body2">
                    {t("demoStudioPage.cooking")}
                  </Typography>
                </Stack>
                {feed.map((line, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {session.specProblems.length > 0 && !busy && (
              <Alert severity="info">
                {t("demoStudioPage.unfinished", {
                  problems: session.specProblems.slice(0, 3).join("; "),
                })}
              </Alert>
            )}

            <TextField
              placeholder={
                session.chat.length
                  ? t("demoStudioPage.askPlaceholder")
                  : t("demoStudioPage.waitingFirstBuild")
              }
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !busy) {
                  e.preventDefault();
                  void send();
                }
              }}
              disabled={busy}
              multiline
              minRows={2}
              fullWidth
              inputProps={{ "data-testid": "demo-message" }}
            />
            <Stack direction="row" spacing={1}>
              <PermissionGate
                resource="demos"
                level="administrate"
                place="nenda"
                deniedProps={{ disabled: true }}
              >
                <Button
                  variant="contained"
                  onClick={send}
                  disabled={busy || !draft.trim()}
                  data-testid="demo-send"
                >
                  {t("common:buttons.send")}
                </Button>
              </PermissionGate>
              <PermissionGate
                resource="demos"
                level="administrate"
                place="nenda"
                deniedProps={{ disabled: true }}
              >
                <Button
                  startIcon={<UndoIcon />}
                  onClick={undo}
                  disabled={busy || !session.canUndo}
                  data-testid="demo-undo"
                >
                  {t("demoStudioPage.undo")}
                </Button>
              </PermissionGate>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">
              {premise?.premiseName ?? t("demoStudioPage.preview")}
              {session.premiseSlug && (
                <Chip
                  size="small"
                  label={t("demoStudioPage.nendaDemo")}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            {premise ? (
              <LivePreview key={previewKey} premise={premise} maxWidth={960} />
            ) : (
              <Typography color="text.secondary">
                {t("demoStudioPage.tvAppears")}
              </Typography>
            )}
            {premise && session.premiseSlug && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  startIcon={<TvIcon />}
                  variant="outlined"
                  onClick={() =>
                    openPreview(
                      session.premiseSlug!,
                      premise.rooms,
                      undefined,
                      room ?? undefined
                    )
                  }
                >
                  {t("demoStudioPage.present")}
                </Button>
                {session.signageScreens[0] && (
                  <Button
                    startIcon={<SignageIcon />}
                    variant="outlined"
                    onClick={() =>
                      openSignagePreview(
                        session.premiseSlug!,
                        session.signageScreens[0].id
                      )
                    }
                  >
                    {t("demoStudioPage.lobbyScreen")}
                  </Button>
                )}
                <Button
                  startIcon={<OpenInNewIcon />}
                  onClick={() => navigate(`/premises/${session.premiseSlug}`)}
                >
                  {t("demoStudioPage.openInCms")}
                </Button>
                <PermissionGate
                  resource="premises"
                  level="administrate"
                  place={{ premiseId: session.premiseSlug }}
                >
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={() => setCloneOpen(true)}
                    disabled={busy}
                    data-testid="demo-clone"
                  >
                    {t("demoStudioPage.cloneToCustomer")}
                  </Button>
                  <Button
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleteOpen(true)}
                    disabled={busy}
                    data-testid="demo-delete"
                  >
                    {t("demoStudioPage.deleteDemo")}
                  </Button>
                </PermissionGate>
              </Stack>
            )}
            {session.premiseSlug && (
              <CloneDialog
                key={cloneOpen ? "open" : "closed"}
                open={cloneOpen}
                demo={premise}
                demoSlug={session.premiseSlug}
                onClose={() => setCloneOpen(false)}
                onCloned={(slug) => navigate(`/premises/${slug}`)}
              />
            )}
            <DangerConfirmDialog
              open={deleteOpen}
              title={t("demoStudioPage.deleteTitle")}
              entityName={session.premiseSlug ?? ""}
              body={t("demoStudioPage.deleteBody")}
              busy={deleteBusy}
              errorMessage={deleteError}
              onCancel={() => setDeleteOpen(false)}
              onConfirm={async () => {
                setDeleteBusy(true);
                setDeleteError(null);
                try {
                  await demoStudioApi.deleteDemo(session.premiseSlug!);
                  navigate("/demo-studio");
                } catch (e) {
                  setDeleteError(friendlyError(t, e));
                } finally {
                  setDeleteBusy(false);
                }
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * "Yes, let's go": a plain copy into a customer's company. No review step -
 * content is redone with the customer's content people at go-live.
 */
function CloneDialog({
  open,
  demo,
  demoSlug,
  onClose,
  onCloned,
}: {
  open: boolean;
  demo: PremiseConfig | null;
  demoSlug: string;
  onClose: () => void;
  onCloned: (slug: string) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [name, setName] = useState(demo?.premiseName ?? "");
  const [slug, setSlug] = useState(demoSlug.replace(/-demo$/, ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    foyerApi
      .listCompanies()
      .then((all) => setCompanies(all))
      .catch((e) => setError(friendlyError(t, e)));
  }, [open, t]);

  async function clone() {
    setBusy(true);
    setError(null);
    try {
      const res = await demoStudioApi.cloneDemo(demoSlug, {
        companyId,
        slug: slug.trim(),
        name: name.trim() || undefined,
      });
      onCloned(res.premiseId);
    } catch (e) {
      setError(
        (e as { code?: string }).code === "slug_taken"
          ? t("demoStudioPage.clone.slugTaken", { slug })
          : friendlyError(t, e)
      );
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t("demoStudioPage.cloneToCustomer")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("demoStudioPage.clone.body")}
          </Typography>
          <TextField
            select
            label={t("demoStudioPage.clone.customer")}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            size="small"
            required
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("demoStudioPage.clone.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
          />
          <TextField
            label={t("demoStudioPage.clone.slug")}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            size="small"
            helperText={t("demoStudioPage.clone.slugHelp")}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={clone}
          disabled={
            busy || !companyId || !/^[a-z0-9][a-z0-9-]*$/.test(slug.trim())
          }
        >
          {busy ? t("demoStudioPage.clone.cloning") : t("common:buttons.clone")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
